import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { PurchaseOrderDetail, PurchaseOrderLine } from '../../../services/purchaseOrders';
import { ModalShell } from '../../shared/ModalShell';
import { MarkShippedModal } from './MarkShippedModal';
import { fmtPoDate } from './poFormat';

function lineState(line: PurchaseOrderLine): string {
    if (line.canceledAt) return `Canceled ${fmtPoDate(line.canceledAt)}`;
    if (line.shippedQuantity <= 0) return 'Not shipped';
    const when = line.shippedAt ? ` · ${fmtPoDate(line.shippedAt)}` : '';
    return `${line.shippedQuantity} of ${line.quantity} shipped${when}`;
}

interface UpdateShippingModalProps {
    purchaseOrder: PurchaseOrderDetail;
    onClose: () => void;
}

// Shipping is tracked per line, so with several lines the operator picks one
// first rather than the button guessing which to edit.
export function UpdateShippingModal({ purchaseOrder, onClose }: UpdateShippingModalProps) {
    const lines = purchaseOrder.lines;
    const [selected, setSelected] = useState<PurchaseOrderLine | null>(
        lines.length === 1 ? lines[0] : null
    );

    if (selected) {
        return (
            <MarkShippedModal
                purchaseOrderId={purchaseOrder.id}
                line={selected}
                onClose={onClose}
            />
        );
    }

    return (
        <ModalShell
            widthClassName="max-w-[520px]"
            panelClassName="rounded-lg p-6"
            dismissible
            onDismiss={onClose}
            labelledBy="po-shipping-pick-title"
        >
            <div className="flex justify-between items-center mb-4">
                <h2 id="po-shipping-pick-title" className="text-xl font-bold text-gray-900">
                    Update shipping
                </h2>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="text-gray-400 hover:text-gray-800 transition-colors"
                >
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </div>

            {lines.length === 0 ? (
                <p className="text-sm text-gray-500">This purchase order has no lines to ship.</p>
            ) : (
                <>
                    <p className="text-sm text-gray-600 mb-3">
                        Pick the line to update: shipping is recorded per line.
                    </p>
                    <div className="flex flex-col gap-2">
                        {lines.map((line) => (
                            <button
                                key={line.id}
                                type="button"
                                disabled={Boolean(line.canceledAt)}
                                onClick={() => setSelected(line)}
                                className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="block text-sm font-semibold text-gray-800 truncate">
                                    {line.productName}
                                </span>
                                <span className="block text-xs text-gray-500 mt-0.5">
                                    {lineState(line)}
                                </span>
                            </button>
                        ))}
                    </div>
                </>
            )}

            <div className="flex justify-end items-center gap-3 mt-6">
                <button
                    type="button"
                    onClick={onClose}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                >
                    Close
                </button>
            </div>
        </ModalShell>
    );
}
